import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";
import TrialBlockedNotice from "@/components/TrialBlockedNotice";
import api from "@/lib/api";
import {
  LayoutDashboard, CalendarCheck, Map, Sparkles, Users,
  Hourglass, BarChart3, Settings as Cog, LogOut, ShieldCheck
} from "lucide-react";

const ALL_ITEMS = [
  { to: "/dashboard", label: () => t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard", roles: "*" },
  { to: "/dashboard/reservations", label: () => t("nav.reservations"), icon: CalendarCheck, testid: "nav-reservations", roles: "*" },
  { to: "/dashboard/floor-plan", label: () => t("nav.floor_plan"), icon: Map, testid: "nav-floor-plan", roles: "*" },
  { to: "/dashboard/lounge", label: () => t("nav.lounge"), icon: Hourglass, testid: "nav-lounge", roles: "*" },
  { to: "/dashboard/services", label: () => t("nav.services"), icon: Sparkles, testid: "nav-services", roles: ["owner", "manager", "admin"] },
  { to: "/dashboard/staff", label: () => t("nav.staff"), icon: Users, testid: "nav-staff", roles: ["owner", "manager", "admin"] },
  { to: "/dashboard/analytics", label: () => t("nav.analytics"), icon: BarChart3, testid: "nav-analytics", roles: ["owner", "manager", "admin"] },
  { to: "/dashboard/settings", label: () => t("nav.settings"), icon: Cog, testid: "nav-settings", roles: ["owner", "manager", "admin"] },
];

function visibleItems(role) {
  return ALL_ITEMS.filter((it) => it.roles === "*" || (Array.isArray(it.roles) && it.roles.includes(role)));
}

const ROLE_LABEL = {
  owner: "Inhaber", manager: "Manager", host: "Host",
  waiter: "Kellner", bartender: "Barkeeper", admin: "Admin",
};

export default function DashboardLayout() {
  const { user, logout, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const [blockedCode, setBlockedCode] = useState(null);

  // Block immediately if /auth/me already reports a blocked subscription.
  // Admins are never blocked (they have no restaurant_id linked normally).
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin" && !user.impersonating) return;
    const status = user.subscription?.status;
    if (status === "trial_expired" || status === "payment_open" || status === "suspended") {
      setBlockedCode(status);
    } else {
      setBlockedCode(null);
    }
  }, [user]);

  // React to runtime 403s from write endpoints
  useEffect(() => {
    const handler = (ev) => {
      const code = ev?.detail?.code;
      if (code) setBlockedCode(code);
    };
    window.addEventListener("sonara:subscription-blocked", handler);
    return () => window.removeEventListener("sonara:subscription-blocked", handler);
  }, []);

  // Trial countdown for the non-blocked trial state
  const trialEndsAt = user?.subscription?.trial_ends_at;
  const trialDaysLeft = (() => {
    if (user?.subscription?.status !== "trial" || !trialEndsAt) return null;
    const ms = new Date(trialEndsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleStopImpersonate = async () => {
    try { await stopImpersonate(); navigate("/admin", { replace: true }); } catch {
      // noop
    }
  };

  // Keep api import used (defensive — webpack tree-shaking)
  void api;

  return (
    <div className="min-h-screen flex bg-[var(--bg-rooftop)] text-[var(--text-primary)]">
      {/* ===== Sidebar — architectural brass spine ===== */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#100D0B] relative">
        {/* Brass hairline running the full height */}
        <span aria-hidden className="absolute top-0 right-0 w-px h-full brass-line-v opacity-90" />

        {/* Logo area */}
        <div className="relative px-7 h-[72px] flex items-center justify-center">
          <Link to="/dashboard" className="block group" data-testid="dashboard-logo">
            <span className="font-heading font-extralight text-[1.25rem] tracking-[0.58em] text-[var(--text-primary)] group-hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
              SONARA
            </span>
          </Link>
          <span aria-hidden className="absolute bottom-0 left-7 right-7 h-px brass-line opacity-80" />
        </div>

        {/* Nav */}
        <nav className="flex-1 py-8 px-4 space-y-0.5">
          {visibleItems(user?.role).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard"}
              data-testid={item.testid}
              className={({ isActive }) =>
                [
                  "group relative flex items-center gap-3 px-3 py-2.5 rounded-sm",
                  "text-[0.82rem] tracking-[0.05em]",
                  "transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                  isActive
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={`w-[15px] h-[15px] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isActive ? "text-[var(--gold)]" : "text-[var(--text-dim)] group-hover:text-[var(--gold-warm)]"
                    }`}
                    strokeWidth={1.4}
                  />
                  <span className="relative">
                    {item.label()}
                    <span
                      aria-hidden
                      className={`absolute left-0 -bottom-1 h-px w-full origin-left transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                      style={{
                        background: "linear-gradient(90deg, transparent, var(--gold), transparent)",
                      }}
                    />
                  </span>
                </>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className="group relative flex items-center gap-3 px-3 py-2.5 mt-6 rounded-sm text-[0.82rem] tracking-[0.05em] text-[var(--gold)]/80 hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            >
              <ShieldCheck className="w-[15px] h-[15px] text-[var(--gold)]" strokeWidth={1.4} />
              <span>{t("nav.admin")}</span>
            </NavLink>
          )}
        </nav>

        {/* Concierge signature — printed business card at the bottom */}
        <div className="relative px-7 pt-8 pb-8">
          <span aria-hidden className="absolute top-0 left-7 right-7 h-px brass-line opacity-50" />
          <div
            className="font-editorial italic text-[1.55rem] leading-none text-[var(--text-primary)]"
            data-testid="user-name"
          >
            {user?.name || "—"}
          </div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--gold-warm)] mt-2.5">
            {ROLE_LABEL[user?.role] || t(`staff.roles.${user?.role}`) || user?.role}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          >
            <LogOut className="w-3 h-3" strokeWidth={1.4} />
            {t("auth.logout")}
          </button>
          {user?.impersonating && (
            <button
              onClick={handleStopImpersonate}
              data-testid="impersonation-stop-btn"
              className="block mt-3 font-editorial italic text-[11.5px] text-[var(--text-dim)] hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            >
              ← Zurück zu SONARA Admin
            </button>
          )}
        </div>
      </aside>

      {/* ===== Mobile top bar ===== */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 glass z-40 flex items-center px-5 justify-between">
        <Link to="/dashboard" className="block">
          <span className="font-heading font-extralight text-base tracking-[0.45em] text-[var(--text-primary)]">SONARA</span>
        </Link>
        <button
          onClick={handleLogout}
          className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          data-testid="mobile-logout"
        >
          {t("auth.logout")}
        </button>
        <span aria-hidden className="absolute bottom-0 left-0 right-0 h-px brass-line opacity-70" />
      </div>

      {/* ===== Main ===== */}
      <main className="flex-1 min-h-screen lg:pt-0 pt-14 overflow-x-hidden">
        {/* Quiet trial countdown — only during active trial, last 14 days */}
        {trialDaysLeft !== null && trialDaysLeft <= 14 && !blockedCode && (
          <div className="px-6 lg:px-12 pt-4">
            <div
              className="rounded-md px-5 py-3 text-[11px] tracking-[0.18em] uppercase inline-flex items-center gap-3"
              style={{
                background: "var(--bg-velvet)",
                border: "1px solid rgba(184,150,86,0.18)",
                color: "var(--text-secondary)",
              }}
              data-testid="trial-countdown-banner"
            >
              <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
              <span>Testphase</span>
              <span className="text-[var(--gold)] font-editorial italic normal-case text-[13px] tracking-normal">
                Noch {trialDaysLeft} {trialDaysLeft === 1 ? "Tag" : "Tage"}
              </span>
            </div>
          </div>
        )}
        <Outlet />
      </main>
      {blockedCode && <TrialBlockedNotice code={blockedCode} />}
    </div>
  );
}
